import _LessonSetViewQuery from "../__generated__/LessonSetViewQuery.graphql";
import { graphql, useLazyLoadQuery } from 'react-relay';
import { Link, Navigate, useParams } from 'react-router-dom';
import { En } from '../i18n/En';
import { t } from '../i18n/t';
import { localDay } from '../lessons/localDay.ts';
import { SET_DAYS } from '../lessons/progress.ts';
import { todayChecklist } from '../lessons/today.ts';
import { registerFor } from '../profile/register';
import { DaySegments, KindTile, SetRing } from './LessonProgress';
import { kindLabel } from './lessonLabels.ts';
import type { LessonSetViewQuery as LessonSetViewQueryType } from '../__generated__/LessonSetViewQuery.graphql';

// The one set by id, not `lessonSets` filtered here: the list would resolve
// `playsToday` and `completedToday` for every lesson of every set (hundreds of
// fields) to show one. An id that is not this learner's resolves to null, the
// same "gone" as a set that no longer exists.
const LessonSetViewQuery = (_LessonSetViewQuery.hash && _LessonSetViewQuery.hash !== "fae4c0465a6c81ca577cae41547953e9" && console.error("The definition of 'LessonSetViewQuery' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _LessonSetViewQuery);

/**
 * One set's lessons in the course's order (lesson library PRD §3, §4.1), a
 * story in parts as A, B, C. Listen is always there; Answer only on a story
 * the server says can be played that way. The head carries the set's ring
 * and, while it is the one followed, its week as segments.
 */
export function LessonSetView() {
  const {
    setId = ''
  } = useParams();
  const today = localDay(new Date());
  const data = useLazyLoadQuery<LessonSetViewQueryType>(LessonSetViewQuery, {
    setId,
    localDay: today
  }, {
    fetchPolicy: 'store-and-network'
  });
  const learner = data.me?.learner;
  if (registerFor(learner?.defaultCharacter?.audience) === 'kid') return <Navigate to="/books" replace />;
  const set = data.node;
  if (!set?.lessons) {
    return <div className="mx-auto flex w-full max-w-2xl flex-col gap-4 p-4">
        <p>{t('lessons-set-gone')}</p>
        <Link to="/lessons" className="link">
          {t('lessons-back')}
        </Link>
      </div>;
  }
  const byId = new Map(set.lessons.map(l => [l.id, l]));
  const rows = todayChecklist(set.lessons, today);
  const playedToday = rows.some(r => r.playsToday > 0);
  const daysPlayed = set.daysPlayed ?? 0;
  const following = set.startedAt != null && set.day != null;
  return <div className="mx-auto flex w-full max-w-2xl flex-col gap-4 p-4">
      <Link to="/lessons" className="link min-h-(--hit-min)">
        {t('lessons-back')}
      </Link>
      <header className="flex flex-col gap-3">
        <div className="flex items-center gap-4">
          <SetRing daysPlayed={daysPlayed} size="3.5rem" />
          <div className="flex flex-col">
            {set.level != null && <span className="text-primary text-xs font-semibold tracking-wide uppercase">
                {t('lessons-level', {
              level: set.level
            })}
              </span>}
            <h1 className="text-2xl font-bold">
              <En>{set.name}</En>
            </h1>
            <span className="text-base-content/60 text-sm">
              {following ? t('lessons-day-of', {
              day: set.day ?? 1,
              total: SET_DAYS
            }) : t('lessons-set-days', {
              days: Math.min(daysPlayed, SET_DAYS),
              total: SET_DAYS
            })}
            </span>
          </div>
        </div>
        {following && <DaySegments day={Math.min(set.day ?? 1, SET_DAYS)} playedToday={playedToday} />}
      </header>
      <ul className="flex flex-col gap-2">
        {rows.map(r => {
        const l = byId.get(r.lessonId);
        if (!l) return null;
        const to = `/lessons/play/${encodeURIComponent(l.id)}`;
        const heard = l.kind === 'MINI_STORY' && r.playsToday > 0;
        return <li key={l.id} className="bg-base-100 border-base-200 flex items-center gap-3 rounded-box border p-3">
              <KindTile kind={l.kind} done={r.done} />
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="font-medium">
                  {t(kindLabel(l.kind))}
                  {l.part && <> {l.part}</>}
                </span>
                {(heard || r.done) && <span className="text-base-content/60 text-xs">
                    {heard ? t('lessons-heard-times', {
                count: r.playsToday
              }) : l.kind === 'ARTICLE' ? t('lessons-read-today') : t('lessons-done')}
                  </span>}
              </span>
              <span className="flex shrink-0 gap-2">
                <Link to={to} className="btn btn-sm btn-outline min-h-(--hit-min)">
                  {l.kind === 'ARTICLE' ? t('lessons-read') : t('lessons-listen')}
                </Link>
                {l.answerReady && <Link to={`${to}?mode=answer`} className="btn btn-sm btn-primary min-h-(--hit-min)">
                    {t('lessons-answer')}
                  </Link>}
              </span>
            </li>;
      })}
      </ul>
    </div>;
}